import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { type Observable, Subject } from 'rxjs';

import { type PendingActions } from '@core/state/pending-actions';

import { CommandFailedEventToken } from '@models/events.model';

import { commandFailureText } from './command-failure-text';
import { EventBusService } from './event-bus.service';
import { ToastService } from './toast.service';

/** A command the server accepted (202) and then could not carry out. */
export interface CommandFailure {
  commandType: string;
  /** Id of the row the command was about, if it has one. */
  entityId: string | null;
  reason: string;
  correlationId: string;
}

/**
 * Every write answers 202 and is confirmed by an event, so a command that fails later would leave the page waiting.
 * The server then sends a CommandFailedEvent to the issuing user. This service says so (one toast, whatever page the
 * user is on by now) and lets pages unlock the row they were waiting for (`release`).
 */
@Injectable({ providedIn: 'root' })
export class CommandFailureService {
  private readonly toast = inject(ToastService);
  private readonly _failures$ = new Subject<CommandFailure>();

  readonly failures$: Observable<CommandFailure> = this._failures$.asObservable();

  constructor() {
    inject(EventBusService)
      .on(CommandFailedEventToken)
      .subscribe((e) => {
        const failure: CommandFailure = {
          commandType: e.commandType,
          entityId: e.entityId ?? null,
          reason: e.reason,
          correlationId: e.correlationId,
        };
        this.toast.error(commandFailureText(failure.commandType, failure.reason));
        this._failures$.next(failure);
      });
  }

  /**
   * Unlocks the rows of `pending` whose command failed: the row looks idle again instead of "Wird verarbeitet"
   * until the timeout. Call in an injection context (constructor or field initializer); ends with the page.
   * `commandTypes` narrows it to commands this list sent, so a failure of another page's command with a
   * clashing id cannot unlock a row.
   */
  release(pending: Pick<PendingActions<unknown>, 'has' | 'settle'>, commandTypes?: readonly string[]): void {
    this.failures$.pipe(takeUntilDestroyed()).subscribe((f) => {
      if (!f.entityId || !pending.has(f.entityId)) {return;}
      if (commandTypes && !commandTypes.includes(f.commandType)) {return;}
      pending.settle(f.entityId);
    });
  }
}
